'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics vs PFAS vs BPA: What's the Difference and Which Should You Worry About?"
      author="MicroPlastics Editorial Team"
      date="May 25, 2026"
      readTime="11 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics vs PFAS vs BPA, what's the difference explained"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <p className="text-lg text-gray-700 leading-relaxed">
        Online wellness culture has flattened &ldquo;toxins in plastic&rdquo;
        into one fear blob. These are three separate problems with three
        separate sources, three different exposure routes, and three
        different reduction strategies. Microplastics are tiny solid
        particles. PFAS are a class of synthetic chemicals used for
        water and grease resistance. BPA is a single industrial
        chemical used to make hard plastic and can liners. Knowing
        which is which lets you make the changes that actually matter
        for your situation.
      </p>

      <BlogTLDR>
        <strong>Microplastics</strong> = solid plastic particles (1 µm
        to 5 mm) and nanoplastics (smaller than 1 µm). They come from
        plastic packaging, textiles, tires, and degraded plastic in the
        environment. <strong>PFAS</strong> = a class of ~15,000 &ldquo;forever
        chemicals&rdquo; used for water / stain / grease resistance, in non-stick cookware, fast-food wrappers, stain-resistant
        fabrics, and many water supplies. <strong>BPA</strong> = one
        specific chemical (bisphenol-A) used to make polycarbonate
        plastic and epoxy can liners. All three appear in food
        packaging; the reductions overlap heavily but aren&apos;t
        identical.
      </BlogTLDR>

      <BlogComparisonTable
        caption="The three at a glance"
        columns={[
          { key: 'what', label: 'What it is' },
          { key: 'mp', label: 'Microplastics' },
          { key: 'pfas', label: 'PFAS' },
          { key: 'bpa', label: 'BPA' },
        ]}
        rows={[
          { what: 'Category', mp: 'Solid plastic particles', pfas: 'Class of ~15,000 synthetic fluorinated chemicals', bpa: 'Single industrial chemical (bisphenol-A)' },
          { what: 'Size', mp: '1 nm to 5 mm', pfas: 'Molecular', bpa: 'Molecular' },
          { what: 'Main sources', mp: 'Bottles, packaging, textiles, tires, cookware', pfas: 'Non-stick pans, water, fast-food wrappers, stain-resistant fabric', bpa: 'Polycarbonate plastic, can liners, thermal receipts' },
          { what: 'Persistence in body', mp: 'Hours to years depending on size', pfas: 'Years (PFOA half-life ~3 yr, PFOS ~5 yr)', bpa: 'Hours (clears via urine)' },
          { what: 'Strongest health evidence', mp: 'Inflammation; cardiovascular markers (2024 NEJM)', pfas: 'Liver, thyroid, immune, kidney, certain cancers', bpa: 'Endocrine disruption (reproductive, metabolic)' },
          { what: 'Filter / reduction', mp: 'Carbon block + RO removes 80-99%', pfas: 'PFAS-rated activated carbon or RO; standard filters miss most', bpa: 'Avoid polycarbonate, thermal receipts, lined cans' },
        ]}
      />

      <BlogKeyTakeaways
        items={[
          <>Microplastics are particles. PFAS and BPA are chemicals. They overlap in sources but require different reduction strategies.</>,
          <>PFAS is the most persistent, once in your body, it stays for years. Reduction now matters more for PFAS than for the others.</>,
          <>BPA clears in hours, exposure today is gone tomorrow, so habit changes work fast.</>,
          <>Microplastics fall in between, small particles can persist in tissues for months to years.</>,
          <>A good water filter and switching from non-stick pans + plastic food storage addresses all three at once.</>,
          <>A microplastic scanner app addresses material and packaging risk, separate tools track PFAS in water and BPA in cans.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What microplastics actually are
      </h2>
      <p>
        Microplastics are solid plastic fragments between 1 micrometer
        (µm) and 5 millimeters (mm). Smaller particles (under 1 µm) are
        called nanoplastics. They come from:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Secondary microplastics</strong>, larger plastic items breaking down: bottles, packaging, tires, paint, textiles.</li>
        <li><strong>Primary microplastics</strong>, manufactured small: microbeads in cosmetics (now mostly banned in US), plastic pellets, glitter.</li>
        <li><strong>Textile fibres</strong>, polyester / nylon clothing sheds during washing and wearing.</li>
        <li><strong>Container shedding</strong>, bottles, takeout, plastic cookware, scratches.</li>
      </ul>
      <p>
        They enter the body through ingestion (water, food) and
        inhalation (indoor and outdoor air). Confirmed detection sites
        in humans now include blood, placenta, lung tissue, liver,
        testes, breast milk, and arterial plaque.
      </p>
      <p>
        See:{' '}
        <Link href="/blog/nanoplastics-vs-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          nanoplastics vs microplastics
        </Link>{' '}
        and{' '}
        <Link href="/blog/microplastics-in-the-brain" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in the brain
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What PFAS actually are
      </h2>
      <p>
        PFAS (per- and polyfluoroalkyl substances) are a class of about
        15,000 synthetic chemicals known for resisting water, grease,
        heat, and stains. The most-studied are PFOA and PFOS (legacy
        chemicals largely phased out in the US but still in the
        environment), and newer replacements like GenX.
      </p>
      <p>
        Common PFAS sources:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Non-stick cookware</strong> (PTFE / Teflon), releases more at high heat or when scratched.</li>
        <li><strong>Stain- and water-resistant fabrics</strong> (Gore-Tex, Scotchgard, Stainmaster carpet, some athletic wear).</li>
        <li><strong>Fast-food wrappers and microwave popcorn bags</strong>, grease-resistant treatments.</li>
        <li><strong>Drinking water</strong>, many US municipal supplies have detectable PFAS (EPA designated PFOA/PFOS hazardous in 2024).</li>
        <li><strong>Cosmetics</strong>, some long-wear / waterproof products contain PFAS.</li>
      </ul>
      <p>
        PFAS persist in the human body for years (PFOA half-life ~3
        years, PFOS ~5 years). The C8 Health Project (Ohio Valley
        cohort) and other studies link PFAS to liver damage, thyroid
        dysfunction, immune suppression, kidney and testicular cancer,
        higher cholesterol, preeclampsia, and lower birth weight.
      </p>
      <p>
        See:{' '}
        <Link href="/blog/pfas-in-dental-floss" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          PFAS in dental floss
        </Link>{' '}
        and{' '}
        <Link href="/blog/nanoplastics-vs-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          nanoplastics vs microplastics
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What BPA (and BPS / BPF) actually are
      </h2>
      <p>
        Bisphenol-A (BPA) is one specific industrial chemical used to:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Make polycarbonate plastic (#7), hard clear plastic; old baby bottles, 5-gallon water jugs.</li>
        <li>Make the epoxy resin liner of metal food cans.</li>
        <li>Coat thermal printer paper (receipts) for color development.</li>
      </ul>
      <p>
        BPA is an endocrine disruptor, it mimics estrogen and binds to
        estrogen receptors in the body. It clears from the body within
        hours through urine, but ongoing daily exposure keeps levels
        steady. The FDA banned BPA in baby bottles and sippy cups in
        2012 but allows it in many other food contact applications.
      </p>
      <p>
        &ldquo;BPA-free&rdquo; products often use BPS (bisphenol-S) or
        BPF (bisphenol-F) instead, both show similar endocrine
        activity in lab studies. &ldquo;BPA-free&rdquo; is a real
        improvement over old polycarbonate, but doesn&apos;t mean
        bisphenol-free.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Where they overlap in food packaging
      </h2>
      <BlogComparisonTable
        caption="Common food contact source, by chemical of concern"
        columns={[
          { key: 'source', label: 'Source' },
          { key: 'mp', label: 'Microplastics?' },
          { key: 'pfas', label: 'PFAS?' },
          { key: 'bpa', label: 'BPA / BPS?' },
        ]}
        rows={[
          { source: 'PET bottled water', mp: 'Yes (high)', pfas: 'Variable (from source water)', bpa: 'No (PET is BPA-free polymer)' },
          { source: 'Canned tomato / acidic food', mp: 'Low', pfas: 'No', bpa: 'Often (epoxy liner)' },
          { source: 'Non-stick cookware', mp: 'PTFE particles when scratched', pfas: 'Yes (high)', bpa: 'No' },
          { source: 'Fast-food wrappers', mp: 'Yes (paperboard with plastic liner)', pfas: 'Often (grease resistance)', bpa: 'No' },
          { source: 'Microwave popcorn bag', mp: 'Yes', pfas: 'Often', bpa: 'No' },
          { source: 'Thermal receipt', mp: 'No', pfas: 'No', bpa: 'Yes (or BPS)' },
          { source: 'Microwaved plastic container', mp: 'Yes (very high)', pfas: 'Some (if PFAS-treated)', bpa: 'If polycarbonate or BPS-replaced' },
          { source: 'Plastic food storage', mp: 'Yes', pfas: 'No (unless treated)', bpa: 'If polycarbonate; otherwise no' },
          { source: 'Coffee K-cup', mp: 'Yes', pfas: 'Low', bpa: 'Verify' },
          { source: 'Tea bag (nylon mesh)', mp: 'Yes (high)', pfas: 'No', bpa: 'No' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical reduction, all three at once
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Filter your drinking water with a PFAS-rated
          filter</strong>, granular activated carbon block (Clearly
          Filtered, Epic, Hydroviv) or reverse osmosis (AquaTru,
          Waterdrop). Removes 80-99% of microplastics AND most PFAS.
          Big multi-target win.
        </li>
        <li>
          <strong>Replace non-stick (PTFE) cookware</strong> with
          stainless, cast iron, or enameled cast iron. Eliminates PFAS
          + cookware-side microplastic shedding.
        </li>
        <li>
          <strong>Switch food storage to glass.</strong> Glass solves
          microplastic shedding and any BPA / BPS concern.
        </li>
        <li>
          <strong>Skip thermal receipts</strong>, request digital or
          decline the receipt. BPA / BPS absorbs through skin within
          seconds.
        </li>
        <li>
          <strong>Avoid fast-food wrappers and microwave popcorn
          bags</strong>, both common PFAS sources.
        </li>
        <li>
          <strong>Skip canned acidic foods</strong> (tomato, soup,
          citrus); pick glass-jarred or fresh. Acid pulls the most
          chemistry out of the liner, whatever the liner is made of.
        </li>
        <li>
          <strong>Never microwave plastic.</strong> Solves microplastic
          + BPS / BPA migration in one step.
        </li>
        <li>
          <strong>Scan food packaging</strong> with the MicroPlastics
          app, addresses material and condition risk specifically.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we do not claim
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>We do not claim that microplastics, PFAS, or BPA at typical exposure levels cause specific diseases in specific individuals.</li>
        <li>We do not provide medical advice. Health concerns should be discussed with a physician.</li>
        <li>We do not claim that any single product (including a scanner app) eliminates exposure, only that informed choices reduce it.</li>
        <li>We do not claim that &ldquo;BPA-free&rdquo; is the same as &ldquo;chemical-free.&rdquo;</li>
        <li>We do not claim that filtering water removes all PFAS, only that PFAS-rated filters significantly reduce common compounds.</li>
        <li>We use published peer-reviewed research and government agency reports as our evidence base. We link the sources so you can read them yourself.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, PVC, polycarbonate, glass, aluminum.</>,
          <>Container condition signals from the photo, scratches, dents, wear.</>,
          <>Brand and product category, flags for known PFAS-coated wrappers, BPA-lined cans, polycarbonate plastic.</>,
          <>Use-context flags you log, microwave, heat, reuse.</>,
          <>The 0–100 risk score is backed by linked published research, including the specific studies cited above for each chemical class.</>,
          <>The app focuses on microplastic risk; PFAS and BPA exposure also matter and are partially addressed through the same packaging signals.</>,
        ]}
      />

      <BlogAppCTA
        headline="One tool, multi-chemical perspective"
        sub="The MicroPlastics app scans for packaging-driven plastic risk. The same packaging often signals PFAS and BPA risk too. Scan to see the score and the linked research."
        buttonLabel="Get the MicroPlastics app"
      />

      <p>
        Related reading:{' '}
        <Link href="/blog/nanoplastics-vs-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          nanoplastics vs microplastics
        </Link>,{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          is BPA-free plastic actually safe?
        </Link>,{' '}
        <Link href="/blog/microplastics-in-canned-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          canned food
        </Link>,{' '}
        <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best non-toxic cookware
        </Link>,{' '}
        <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          water filters compared
        </Link>, and the regulatory side in the{' '}
        <Link href="/blog/eu-microplastics-ban" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          EU microplastics ban
        </Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'What\'s the difference between microplastics, PFAS, and BPA?',
            answer:
              'Microplastics are solid plastic particles (1 nm to 5 mm) that shed from packaging, textiles, and the environment. PFAS are a class of ~15,000 synthetic "forever chemicals" used for water/grease/stain resistance, common in non-stick cookware and drinking water. BPA is one specific industrial chemical used in polycarbonate plastic and metal can liners. All three appear in food packaging but require different reduction strategies.',
          },
          {
            question: 'Which is the biggest health concern?',
            answer:
              'PFAS has the most comprehensive established health evidence, linked to liver, thyroid, immune, kidney, and certain cancer risks across multiple large cohort studies. PFAS also persists in the body for years. Microplastics are a rapidly growing concern (2024 NEJM linked arterial plaque microplastics to higher heart attack risk). BPA endocrine disruption is well-documented in animals and lab studies; human evidence is growing.',
          },
          {
            question: 'Does "BPA-free" mean safe?',
            answer:
              'Only specifically for BPA. "BPA-free" plastics often contain BPS (bisphenol-S) or BPF (bisphenol-F) replacements, which show similar endocrine activity in lab studies. The plastic itself still sheds microplastics. The real upgrades are glass, stainless steel, and aluminum.',
          },
          {
            question: 'Do water filters remove all three?',
            answer:
              'A good carbon-block or reverse-osmosis filter removes 80-99% of microplastics. PFAS-rated filters (NSF P473) significantly reduce common PFAS compounds. BPA is generally not an issue in drinking water itself, it migrates from polycarbonate jugs (rare now) and from canned beverages, so the filter doesn\'t address those. Filtering tap water is still the single best step for chemical reduction.',
          },
          {
            question: 'Which is most persistent in the body?',
            answer:
              'PFAS, by far. PFOA has a serum half-life of about 3 years, PFOS about 5 years. BPA clears within hours. Microplastic particles fall in between, depending on size and tissue distribution, particles can persist for months to years in some tissues.',
          },
          {
            question: 'Do these chemicals affect children more than adults?',
            answer:
              'Yes. Children receive a much higher dose per kilogram of body weight, their organs clear chemicals less efficiently, and developing endocrine and nervous systems are more sensitive to disruption. This is why baby and child products get extra attention in the research and in safe-product certifications.',
          },
          {
            question: 'How does the MicroPlastics app help with all three?',
            answer:
              'The app scans packaging and rates microplastic risk based on material, condition, brand, and category. The same packaging signals often correlate with PFAS exposure (PFAS-treated wrappers) and BPA exposure (polycarbonate or lined cans). It is not a PFAS detector or a BPA test kit, but it provides a useful first-pass risk filter at the point of purchase.',
          },
          {
            question: 'Are there separate apps for PFAS and BPA?',
            answer:
              'PFAS in your local tap water can be checked through the EWG Tap Water Database. BPA exposure is best reduced through behavior (skip polycarbonate, lined cans, thermal receipts) rather than scanning. The microplastic scanner addresses the daily packaging decision that\'s hardest to track manually.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and Nanoplastics in Atheromas and Cardiovascular Events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Steenland K, Fletcher T, Savitz DA',
            year: 2010,
            title: 'Epidemiologic evidence on the health effects of perfluorooctanoic acid (PFOA)',
            publisher: 'Environmental Health Perspectives',
            url: 'https://ehp.niehs.nih.gov/doi/10.1289/ehp.0901827',
          },
          {
            authors: 'Vandenberg LN, Hauser R, Marcus M, et al.',
            year: 2007,
            title: 'Human exposure to bisphenol A (BPA)',
            publisher: 'Reproductive Toxicology',
            url: 'https://pubmed.ncbi.nlm.nih.gov/17707979/',
          },
          {
            authors: 'US EPA',
            year: 2024,
            title: 'Per- and Polyfluoroalkyl Substances (PFAS): Final Drinking Water Standard',
            publisher: 'US EPA',
            url: 'https://www.epa.gov/sdwa/and-polyfluoroalkyl-substances-pfas',
          },
          {
            authors: 'US FDA',
            year: 2024,
            title: 'Bisphenol A (BPA): Use in Food Contact Application',
            publisher: 'FDA',
            url: 'https://www.fda.gov/food/food-packaging-other-substances-come-contact-food-information-consumers/bisphenol-bpa-use-food-contact-application',
          },
          {
            authors: 'Environmental Working Group',
            year: 2024,
            title: 'EWG\'s Tap Water Database',
            publisher: 'EWG',
            url: 'https://www.ewg.org/tapwater/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
